import { forwardRef, useState } from 'react'

import { styled } from 'styled-components'

import { withLabel, withFormValue } from 'peach/decorators'
import { formatOptions } from 'peach/helpers'
import color from 'peach/helpers/color/color'

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding: 8px;
`

const Item = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  padding: 0 8px;
`

const RADIUS = 3

const Radio = styled.input`
  appearance: none;
  display: inline-block;
  position: relative;
  transition: box-shadow 0.2s, background-color 0.2s, border-color 0.2s;
  margin: 0;
  outline: none;
  border: 1px solid ${(p) => color.deepen(p.theme.border)};
  border-radius: 50%;
  box-shadow: 0 0 0 1px transparent;
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  line-height: 2;

  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    transition: background-color 0.2s, border-color 0.2s;
    opacity: 0.8;
    margin-top: ${`-${RADIUS}px`};
    margin-left: ${`-${RADIUS}px`};
    border: ${`${RADIUS}px solid transparent`};
    border-radius: 50%;
    border-color: ${(p) => p.theme.banner};
    width: ${`${2 * RADIUS}px`};
    height: ${`${2 * RADIUS}px`};
    content: '';
  }

  &:checked::after {
    transform: scale(1.5);
    border-color: ${(p) => p.theme.highlight};
  }

  &:focus {
    border-color: ${(p) => p.theme.highlight};
  }
`

const Label = styled.label`
  display: inline-block;
  cursor: pointer;
  padding-left: 8px;
  line-height: 1;
  color: ${(p) => p.theme.text};
`

const renderNullItem = (opts) => {
  const { nullable, nullLabel, groupName, selectedValue, onChange } = opts

  if (!nullable) return null

  const id = `${groupName}-null`
  const label = nullLabel || 'Null'

  return (
    <Item key={id}>
      <Radio
        name={groupName}
        id={id}
        type='radio'
        checked={selectedValue === null}
        value={label}
        onChange={() => onChange(null)}
      />
      <Label htmlFor={id}>{label}</Label>
    </Item>
  )
}

const RadioSelect = forwardRef((props, ref) => {
  const {
    onChange,
    value: selectedValue,
    options,
    nullable,
    nullLabel,
    notes,
    disabled,
  } = props

  const $options = formatOptions(options, { notes, disabled })

  const [groupName] = useState(() => _.uniqueId('radio-select-'))

  const radios = _.map($options, ({ label, value }) => {
    const isSelected = value === selectedValue
    const radioId = `${groupName}-value-${value}`
    return (
      <Item key={radioId}>
        <Radio
          name={groupName}
          id={radioId}
          type='radio'
          checked={isSelected}
          value={value}
          onChange={() => onChange(value)}
        />
        <Label htmlFor={radioId}>{label}</Label>
      </Item>
    )
  })

  const nullItem = renderNullItem({
    nullable,
    nullLabel,
    groupName,
    selectedValue,
    onChange,
  })

  return (
    <Wrapper ref={ref}>
      {radios}
      {nullItem}
    </Wrapper>
  )
})

export { RadioSelect as RawRadioSelect }

export default withFormValue(withLabel(RadioSelect))
